// frontend/src/components/loading/AwaitPageSkeleton.js
//
// Holds the branded loader until a route's code-split page skeleton can draw,
// so a cold boot straight onto that route goes splash -> navbar + skeleton ->
// page, with no empty page body in between.
//
//   <ProtectedRoute requireAdmin>
//     <AwaitPageSkeleton skeleton={LazyAdminPageSkeleton} preloadPage={AdminDashboard.preload}>
//       <AuthLayout>
//         <PageBodySuspense fallback={<LazyAdminPageSkeleton />}>
//           <AdminDashboard />
//         ...
//
// WHY. The admin skeleton is a chunk of its own (LazyAdminPageSkeleton.js) and
// ProtectedRoute starts it only once the session says "admin" - the same moment
// this route renders. AuthLayout commits at once (the page body sits in
// PageBodySuspense inside it), so on a cold boot the navbar and footer showed
// over an EMPTY body (utils/lazySkeleton.js rule 1: nothing while the skeleton's
// own chunk is on its way) for as long as that chunk took - measured ~300ms on
// the dev server at /admin?tab=1. Here the layout is not rendered until the
// skeleton is in, and meanwhile the screen keeps what it already showed: the
// branded loader (BrandBootLoader - the in-app twin of the boot splash, which
// ProtectedRoute itself shows while the session is checked, on the same breath
// clock). On an authenticated boot the splash hands over to that loader
// (ProtectedRoute's ReleaseAuthBootOnMount commits with it) - the crossfade
// BrandBootLoader is built for, so the handover does not show.
//
// NO SERIALISED WAIT. The page chunk does not wait for the skeleton chunk:
// `preloadPage` starts it in the same effect, so both download side by side.
//
// BOUNDED, NEVER BLOCKING. The gate opens when the skeleton preload settles -
// delivered OR failed (`.preload()` never rejects; a failed skeleton renders
// nothing, exactly as before) - or after SKELETON_WAIT_CAP_MS, whichever comes
// first, so a crawling chunk degrades to the old empty-body moment, never to a
// longer wait. Once the skeleton is in (the usual case: ProtectedRoute warmed
// it on an earlier page of the session) the gate renders its children on the
// first render - an in-app navigation sees no loader at all.
//
// Never throws; renders no text of its own (BrandBootLoader carries the one
// hidden loading label).

import React, { useEffect, useState } from "react";
import BrandBootLoader from "../BrandBootLoader";

/** The longest the gate holds the loader for a skeleton chunk (ms). */
export const SKELETON_WAIT_CAP_MS = 1500;

/**
 * @param {object} props
 * @param {{preload: () => Promise<unknown>, isReady: () => boolean}} props.skeleton
 *        a lazySkeleton() component (utils/lazySkeleton.js)
 * @param {() => unknown} [props.preloadPage] starts the page's own chunk; its
 *        failure is left to the route's lazy render (it is swallowed here)
 * @param {React.ReactNode} props.children the route's layout + page
 */
function AwaitPageSkeleton({ skeleton, preloadPage, children }) {
  const [ready, setReady] = useState(() => {
    try {
      return Boolean(skeleton.isReady());
    } catch (e) {
      return true;
    }
  });

  useEffect(() => {
    if (ready) return undefined;
    let live = true;
    const open = () => {
      if (live) setReady(true);
    };
    try {
      if (preloadPage) Promise.resolve(preloadPage()).catch(() => undefined);
    } catch (e) {
      /* A preload is an optimisation: the route loads its page either way. */
    }
    let timer = null;
    try {
      Promise.resolve(skeleton.preload()).then(open, open);
      timer = setTimeout(open, SKELETON_WAIT_CAP_MS);
    } catch (e) {
      open();
    }
    return () => {
      live = false;
      if (timer !== null) clearTimeout(timer);
    };
  }, [ready, skeleton, preloadPage]);

  return ready ? children : <BrandBootLoader />;
}

export default AwaitPageSkeleton;
